import React, { useState } from 'react';
import './DebateLikertPopup.css';
import { CHAT_DURATION_SECONDS } from '../../gameConfig';

const CHAT_ENGAGEMENT_MINUTES = Math.round(CHAT_DURATION_SECONDS / 60);

export type DebateLikertTiming = 'pre' | 'post';

interface DebateLikertPopupProps {
  timing: DebateLikertTiming;
  question: string;
  onSubmit: (rating: number) => void | Promise<void>;
  isSubmitting?: boolean;
}

const DebateLikertPopup: React.FC<DebateLikertPopupProps> = ({
  timing,
  question,
  onSubmit,
  isSubmitting = false,
}) => {
  const [selected, setSelected] = useState<number | null>(null);
  const [preStep, setPreStep] = useState<'question' | 'engagement'>('question');

  const heading = timing === 'pre' ? 'Pre-Discussion Question' : 'Post-Discussion Question';
  const showEngagementStep = timing === 'pre' && preStep === 'engagement';

  const handleContinue = () => {
    if (isSubmitting) return;

    if (timing === 'pre' && preStep === 'question') {
      if (selected == null) return;
      setPreStep('engagement');
      return;
    }

    if (selected == null) return;
    onSubmit(selected);
  };

  return (
    <div className="debate-likert-overlay" role="dialog" aria-modal="true" aria-labelledby="debate-likert-heading">
      <div className={`debate-likert-card debate-likert-card-${timing}`}>
        <div className="debate-likert-accent" aria-hidden="true" />
        <h2 id="debate-likert-heading" className="debate-likert-heading">
          {showEngagementStep ? 'Before You Start the Discussion' : heading}
        </h2>

        {showEngagementStep ? (
          <p className="debate-likert-question debate-likert-engagement-notice">
            This is part of the task. You must engage consistently in the chat for{' '}
            <strong>{CHAT_ENGAGEMENT_MINUTES} minutes</strong>. Otherwise, your task may be rejected due to lack of
            engagement in the chat for Task 1.
          </p>
        ) : (
          <>
            <p className="debate-likert-question">{question}</p>

            <div className="debate-likert-scale-wrap">
              <div className="debate-likert-scale-labels">
                <span>totally disagree</span>
                <span>totally agree</span>
              </div>
              <div className="debate-likert-scale" role="radiogroup" aria-label="Rating from 1 to 7">
                {([1, 2, 3, 4, 5, 6, 7] as const).map((value) => (
                  <button
                    key={value}
                    type="button"
                    className={`debate-likert-option${selected === value ? ' selected' : ''}`}
                    onClick={() => setSelected(value)}
                    disabled={isSubmitting}
                    aria-pressed={selected === value}
                    aria-label={`${value} of 7`}
                  >
                    {value}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        <button
          type="button"
          className="debate-likert-continue"
          disabled={(showEngagementStep ? false : selected == null) || isSubmitting}
          onClick={handleContinue}
        >
          {isSubmitting ? 'Saving…' : showEngagementStep ? 'Start Discussion' : 'Continue'}
        </button>
      </div>
    </div>
  );
};

export default DebateLikertPopup;
